import React from "react";
import tryParseNumber from "assets/scripts/utils/tryParseNumber";
import { FormattedMessage } from "react-intl";
import moment from "moment";
import { arrayToDate } from "assets/scripts/components/forms/formHelpers";
import accountTypeOptions from "./accountTypeOptions";
import isEmpty from "assets/scripts/utils/isEmpty";
import validZip from "assets/scripts/utils/validZip";
import validPhone from "assets/scripts/utils/validPhone";
import validEmail from "assets/scripts/utils/validEmail";
import { ERRORS, REQUIRED } from "assets/scripts/constants/FormMessages";
import aba from "abavalidator";
import getStateError from "assets/scripts/utils/getStateError";
import getMaxLengthError from "assets/scripts/utils/getMaxLengthError";
import keyBy from "lodash/keyBy";
function getAmountError(amountString, allowZero, maxAmountFn, ignoreAmount) {
    const amount = tryParseNumber(amountString);
    if (amount == null) {
        return REQUIRED;
    }
    const maxAmount = maxAmountFn();
    if (ignoreAmount) {
        if (amount <= 0) {
            return (<FormattedMessage id="forms.payNow.amount.zero" defaultMessage="Please enter a value greater than $0"/>);
        }
    }
    else if (amount < 0 || (amount === 0 && !allowZero) || amount > maxAmount) {
        return (<FormattedMessage id="forms.payNow.amount.error" defaultMessage="Please enter a value between $0 and {amount, number, usd}" values={{
            amount: maxAmount
        }}/>);
    }
    return null;
}
export function showDate(group, isSettlement) {
    return group.primary.allowScheduledPayment && !isSettlement;
}
export function getDateRange(now = moment()) {
    const today = now.clone().startOf("day");
    return {
        minDate: today,
        maxDate: today.clone().add(45, "days")
    };
}
function getDateError(date, group, now, isSettlement) {
    if (showDate(group, isSettlement)) {
        const dateMoment = arrayToDate(date);
        const { minDate, maxDate } = getDateRange(now);
        if (dateMoment == null) {
            return REQUIRED;
        }
        if (minDate.isAfter(dateMoment)) {
            return (<FormattedMessage id="forms.messages.errors.scheduleddate.past" defaultMessage="Past dates not allowed"/>);
        }
        if (maxDate.isBefore(dateMoment)) {
            return (<FormattedMessage id="forms.messages.errors.scheduleddate.future" defaultMessage="Payments cannot be scheduled more than 45 days in advance"/>);
        }
    }
    return null;
}
export function showRoutingAndAccountNumbers(value) {
    return !process.env.FEATURE_SAVE_BANK_INFO || value.bankInfoId === "-1";
}
export function showAccountType(value) {
    return showRoutingAndAccountNumbers(value) && Object.keys(accountTypeOptions).length > 1;
}
function getRequiredStringError(value) {
    return isEmpty(value) ? REQUIRED : null;
}
export function getPaymentInfoErrors(value, group, now, isSettlement, ignoreAmount) {
    let amountsValid = true;
    let amountsGlobalError = null;
    const amountErrorsById = {};
    if (!value.distribute) {
        const acctMap = keyBy(group.accounts, it => it.id);
        let hasValue = false;
        for (const key of Object.keys(value.amounts)) {
            const acctId = parseInt(key, 10);
            const amount = value.amounts[acctId];
            const error = getAmountError(amount, true, () => acctMap[acctId].maxPayment, ignoreAmount);
            amountsValid = amountsValid && error == null;
            amountErrorsById[acctId] = error;
            if (!hasValue) {
                const parsedAmount = tryParseNumber(amount);
                if (parsedAmount != null && parsedAmount > 0) {
                    hasValue = true;
                }
            }
        }
        if (!hasValue && amountsValid) {
            amountsGlobalError = REQUIRED;
        }
    }
    const amountError = value.distribute && !isSettlement
        ? getAmountError(value.amount, false, () => getAmountForRouteGroup(group).maxPayment, ignoreAmount)
        : null;
    const dateError = getDateError(value.date, group, now, isSettlement);
    return {
        valid: amountsValid && amountsGlobalError == null && amountError == null && dateError == null,
        amount: amountError,
        date: dateError,
        amounts: {
            global: amountsGlobalError,
            byId: amountErrorsById
        }
    };
}
export function trimBankInfo(value) {
    return {
        ...value,
        routingNumber: value.routingNumber.trim(),
        accountNumber: value.accountNumber.trim()
    };
}
export function getBankInfoErrors(value) {
    value = trimBankInfo(value);
    const checkRoutingAndAccountNumbers = showRoutingAndAccountNumbers(value);
    return {
        routingNumber: checkRoutingAndAccountNumbers ? getRoutingNumberError(value.routingNumber, value.accountType) : null,
        accountNumber: checkRoutingAndAccountNumbers && !/^\d{4,17}$/.test(value.accountNumber)
            ? ERRORS.ACCOUNT_NUMBER : null,
        accountType: showAccountType(value) && isEmpty(value.accountType) ? REQUIRED : null,
        name: getMaxLengthError(value.bankInfoName, 50)
    };
}
export function getPersonalInfoErrors(value, hideEmail) {
    return {
        firstName: getRequiredStringError(value.firstName) || getMaxLengthError(value.firstName, 100),
        lastName: getRequiredStringError(value.lastName) || getMaxLengthError(value.lastName, 100),
        address: getRequiredStringError(value.address) || getMaxLengthError(value.address, 100),
        address2: getMaxLengthError(value.address2, 100),
        city: getRequiredStringError(value.city) || getMaxLengthError(value.city, 100),
        state: getStateError(value.state, true),
        zip: isEmpty(value.zip) || !validZip(value.zip) ? ERRORS.ZIP : null,
        phone: isEmpty(value.phone) || !validPhone(value.phone) ? ERRORS.PHONE : null,
        email: !!hideEmail ? null : isEmpty(value.email) || !validEmail(value.email) ? ERRORS.EMAIL : getMaxLengthError(value.email, 100)
    };
}
const R_CHECKING = /^[0-3]/;
function getRoutingNumberError(routingNumber, accountType) {
    if (!routingNumber || !/^\d{9}$/.test(routingNumber) || !aba.validate(routingNumber)) {
        return (<FormattedMessage id="forms.messages.errors.routingNumber" defaultMessage="Please enter a valid routing number"/>);
    }
    if (accountType === "c" && !R_CHECKING.test(routingNumber)) {
        return (<FormattedMessage id="forms.messages.errors.routingNumber.checking" defaultMessage="Invalid routing number for a checking account"/>);
    }
    return null;
}
export function getAmountForRouteGroup(group) {
    const result = group.accounts
        .filter(it => it.paymentEligible)
        .reduce((acc, { maxPayment, amountDue }) => {
        // sum as cents to avoid floating point rounding errors
        return {
            maxPayment: acc.maxPayment + (100 * maxPayment),
            amountDue: acc.amountDue + (100 * amountDue)
        };
    }, {
        maxPayment: 0,
        amountDue: 0
    });
    // back to dollars
    return {
        maxPayment: result.maxPayment / 100,
        amountDue: result.amountDue / 100
    };
}
export function allowIndividualAccountPayment(group) {
    return group.primary.paymentFrequency !== "SET";
}



// WEBPACK FOOTER //
// ./src/assets/scripts/components/forms/secure/PayNowForm/utils.tsx